import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { map, take } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { redirectMerchantUserToPortal } from '../utils/merchant-portal';

function getReturnUrl(path: string): string {
  if (!path || path === '/login' || path === '/verify-email' || path === '/change-password') {
    return '/app';
  }
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Guard that checks if user has local auth state.
 * If no local state exists, redirects to /login immediately.
 * Use this for quick checks without server validation.
 *
 * Note: Session cookies are HttpOnly and cannot be read by JavaScript.
 * This guard checks the local auth state (localStorage/signal) instead.
 */
export const noAuthGuard: CanMatchFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Check if we have any local auth state
  if (!auth.isAuthenticated()) {
    router.navigate(['/login'], {
      queryParams: { returnUrl: getReturnUrl(router.url) },
    });
    return false;
  }

  if (redirectMerchantUserToPortal(auth.user())) {
    return false;
  }

  return true;
};

/**
 * Guard that allows access only when authenticated. Redirects to /login otherwise.
 * Validates with the server to ensure session is still valid.
 *
 * Merchant-role users are redirected to the Merchant Portal (not this admin app).
 *
 * Flow:
 * 1. If local user exists, allow access (server will return 401 if session expired)
 * 2. If no local user, try to fetch from server (validates session cookie)
 * 3. If server returns null/error, redirect to login
 */
export const authGuard: CanMatchFn = (route, segments) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const requestedPath = segments.length > 0 ? `/${segments.map((segment) => segment.path).join('/')}` : '/app';
  const returnUrl = getReturnUrl(requestedPath);

  return auth.resolveUser().pipe(
    take(1),
    map((user) => {
      if (user) {
        if (redirectMerchantUserToPortal(user)) {
          return false;
        }
        const isChangePasswordRoute = route.path === 'change-password';
        if (user.password_change_required && !isChangePasswordRoute) {
          router.navigate(['/change-password']);
          return false;
        }
        if (!user.password_change_required && isChangePasswordRoute) {
          router.navigate(['/app']);
          return false;
        }
        const isVerifyEmailRoute = route.path === 'verify-email';
        if (user.email_verified === false && !isVerifyEmailRoute) {
          router.navigate(['/verify-email']);
          return false;
        }
        if (user.email_verified !== false && isVerifyEmailRoute) {
          router.navigate(['/app']);
          return false;
        }
        return true;
      }
      auth.clearLocalState();
      router.navigate(['/login'], {
        queryParams: { returnUrl },
      });
      return false;
    })
  );
};

/**
 * Guard for login route: redirect to /app if already authenticated (system roles),
 * or to the Merchant Portal when the effective role is merchant-only.
 */
export const loginGuard: CanMatchFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.resolveUser().pipe(
    take(1),
    map((user) => {
      if (!user) return true;
      if (redirectMerchantUserToPortal(user)) {
        return false;
      }
      if (user.password_change_required) {
        router.navigate(['/change-password']);
        return false;
      }
      if (user.email_verified === false) {
        router.navigate(['/verify-email']);
        return false;
      }
      router.navigate(['/app']);
      return false;
    })
  );
};
